import {
  createInvoicesAndBookingsFromEstimateList,
  deleteEstimatesFromEstimateIdList,
  deleteInvoicesFromInvoiceIdList,
  duplicateEstimateFromEstimateIdList,
  markInvoiceAsPaid,
  sendEstimateEmail,
  sendInvoiceEmail,
  checkInvoicesExistOnStripe,
} from "../../store/actions/estimateActions";
import { UserContext } from "../../App";
import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { OutlineButton } from "../../components/layout/OutlineButton";
import { FooterActionsContainer } from "../../components/layout/Footer";
import SelectContactsModal from "./SelectContactsModal";
import { db } from "../../index";

export default function Footer({
  readOnly,
  cannotDelete,
  isEstimate,
  rawObject,
  loading,
  setLoading,
  createButton,
}) {
  const { userInfos } = useContext(UserContext);
  const history = useHistory();
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // region action buttons
  async function duplicateObj(e) {
    e.preventDefault();

    if (isEstimate) {
      if (!window.confirm("Etes-vous sûr de vouloir dupliquer ce devis ?"))
        return;

      setLoading(true);
      await duplicateEstimateFromEstimateIdList([rawObject.id], userInfos.jwt);
      window.alert("Devis dupliqué avec succès");
      history.push("/estimates");
      setLoading(false);
    }
  }

  async function deleteObj(e) {
    e.preventDefault();

    if (isEstimate) {
      if (!window.confirm("Etes-vous sûr de vouloir supprimer ce devis ?"))
        return;
      setLoading(true);
      await deleteEstimatesFromEstimateIdList([rawObject.id], userInfos.jwt);
      window.alert("Devis supprimé avec succès");
      history.push("/estimates");
      setLoading(false);
    } else {
      // Check existence on Stripe first
      const checkResponse = await checkInvoicesExistOnStripe([rawObject.id], userInfos.jwt);
      const stripeCheck = checkResponse.data[0];
      
      const statusLabel = rawObject.status === "draft" ? "brouillon" : 
                         rawObject.status === "open" ? "ouverte" :
                         rawObject.status === "sent" ? "envoyée" :
                         rawObject.status === "paid" ? "payée" : rawObject.status;
      const stripeStatus = stripeCheck.existsOnStripe ? "" : " - n'existe pas sur Stripe";
      
      if (!window.confirm(
        `Êtes-vous sûr de vouloir supprimer cette facture ?\n\nFacture: ${rawObject.id}\nStatut: ${statusLabel}${stripeStatus}`
      ))
        return;
      
      setLoading(true);
      const response = await deleteInvoicesFromInvoiceIdList([rawObject.id], userInfos.jwt);
      const results = response.data;
      const result = results[0];
      
      if (result && result.success) {
        window.alert(result.message);
      } else if (result && !result.success) {
        window.alert(result.message);
      } else {
        window.alert("Facture supprimée avec succès");
      }
      history.push("/invoices");
      setLoading(false);
    }
  }

  async function sendObjByEmail(selectedContacts) {
    setIsSending(true);
    setLoading(true);
    try {
      if (isEstimate) {
        await sendEstimateEmail(rawObject.id, userInfos.jwt, selectedContacts);
        window.alert("Devis envoyé avec succès");
        history.push("/estimates");
      } else {
        await sendInvoiceEmail(rawObject.id, userInfos.jwt, selectedContacts);
        window.alert("Facture envoyée avec succès");
        history.push("/invoices");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      const apiError =
        typeof error?.response?.data === "string"
          ? error.response.data
          : error?.response?.data?.error ||
            error?.response?.data?.message ||
            "";
      if (!apiError) {
        window.alert("Erreur lors de l'envoi de l'email");
      }
    } finally {
      setIsSending(false);
      setLoading(false);
    }
  }

  async function onClickSendObjByEmail(e) {
    e.preventDefault();

    // Check if the customer has contacts before showing the modal
    const snapshot = await db
      .ref(`users/${userInfos.uid}/provider/${rawObject.customer.id}`)
      .once("value");

    const providerData = snapshot.val();
    if (
      providerData &&
      providerData.contacts &&
      providerData.contacts.length > 0
    ) {
      // If there's at least one contact, show the modal
      setShowContactsModal(true);
    } else {
      // If no contacts, call handleSendEmail directly with empty array
      await sendObjByEmail();
    }
  }

  async function handleSendEmail(selectedContacts) {
    await sendObjByEmail(selectedContacts);
    setShowContactsModal(false);
  }

  async function createInvoiceAndBookingsFromEstimate(e) {
    e.preventDefault();

    if (isEstimate) {
      if (
        !window.confirm(
          "Etes-vous sûr de vouloir créer la facture et les courses ?"
        )
      )
        return;

      setLoading(true);
      try {
        await createInvoicesAndBookingsFromEstimateList(
          [rawObject.id],
          userInfos.jwt
        );
        window.alert("Facture et courses créées avec succès");
        history.push("/estimates");
      } catch (error) {
        const apiError =
          typeof error?.response?.data === "string"
            ? error.response.data
            : error?.response?.data?.error ||
              error?.response?.data?.message ||
              "";
        if (!apiError) {
          window.alert(
            "Erreur lors de la création de la facture et des courses"
          );
        }
      } finally {
        setLoading(false);
      }
    }
  }

  async function markAsPaid(e) {
    e.preventDefault();

    if (
      !window.confirm(
        "Etes-vous sûr de vouloir marquer cette facture comme payée ?"
      )
    )
      return;

    setLoading(true);
    try {
      await markInvoiceAsPaid(rawObject.id, userInfos.jwt);
      window.alert("Facture marquée comme payée avec succès");
      history.push("/invoices");
    } catch (error) {
      const apiError =
        typeof error?.response?.data === "string"
          ? error.response.data
          : error?.response?.data?.error ||
            error?.response?.data?.message ||
            "";
      if (!apiError) {
        window.alert(
          "Erreur lors du marquage de la facture comme payée. Veuillez réessayer."
        );
      }
    } finally {
      setLoading(false);
    }
  }

  // endregion

  return (
    <>
      <FooterActionsContainer>
        <div>
          <OutlineButton
            type="button"
            disabled={loading}
            onClick={(e) => {
              e.preventDefault();
              history.goBack();
            }}
          >
            <i className="fas fa-arrow-left" />
            Retour
          </OutlineButton>
          {["draft", "open", "sent", "accepted"].indexOf(rawObject?.status) !==
            -1 && (
            <>
              <OutlineButton onClick={onClickSendObjByEmail} disabled={loading}>
                Envoyer par mail
              </OutlineButton>
              {isEstimate && (
                <OutlineButton
                  onClick={createInvoiceAndBookingsFromEstimate}
                  disabled={loading}
                >
                  Créer la facture (non payée) et les courses
                </OutlineButton>
              )}
              {!isEstimate && (
                <OutlineButton onClick={markAsPaid} disabled={loading}>
                  Marquer comme payée
                </OutlineButton>
              )}
            </>
          )}
        </div>
        <div>{!readOnly && createButton}</div>
        <div>
          {isEstimate && (
            <OutlineButton onClick={duplicateObj} disabled={loading}>
              <i className="fas fa-copy" />
              Dupliquer
            </OutlineButton>
          )}
          {!isEstimate && (
            <OutlineButton onClick={deleteObj} disabled={loading}>
              <i className="fas fa-trash-alt" />
              Supprimer
            </OutlineButton>
          )}
          {isEstimate && !cannotDelete && (
            <OutlineButton onClick={deleteObj} disabled={loading}>
              <i className="fas fa-trash-alt" />
              Supprimer
            </OutlineButton>
          )}
        </div>
      </FooterActionsContainer>

      <SelectContactsModal
        show={showContactsModal}
        setShow={setShowContactsModal}
        estimate={isEstimate ? rawObject : null}
        invoice={!isEstimate ? rawObject : null}
        submit={handleSendEmail}
        fetching={isSending}
      />
    </>
  );
}
